import { useEffect, useState } from "react";
import { CheckCircle2, ChevronDown, ChevronUp, Circle, Pencil, Plus, Trash2, UserPlus } from "lucide-react";
import { ColorBlock, BigStat } from "@/components/page";
import {
  useResourceList,
  useResourceCreate,
  useResourceUpdate,
  useResourceDelete,
} from "@/components/data";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

type ClinicExtra = {
  rate?: number;
  payer?: string;
  payer_color?: string;
  note?: string;
  paid_months?: string[];
};

type Contact = {
  id: number;
  first_name: string | null;
  last_name: string | null;
  lifecycle_stage: string | null;
  extra: ClinicExtra | null;
};

type ClinicSession = {
  id: number;
  contact_id: number;
  session_date: string;
  notes: string | null;
};

const STATUS_MAP: Record<string, { label: string; dot: string }> = {
  clinic_patient: { label: "פעיל", dot: "bg-emerald-400" },
  clinic_archive: { label: "ארכיון", dot: "bg-stone-400" },
  clinic_intake: { label: "קליטה", dot: "bg-amber-400" },
  clinic_paused: { label: "מושהה", dot: "bg-sky-400" },
};

const STAGE_OPTIONS = [
  { value: "clinic_patient", label: "פעיל" },
  { value: "clinic_archive", label: "ארכיון" },
  { value: "clinic_paused", label: "מושהה" },
];

function statusOf(stage: string | null): { label: string; dot: string } {
  if (!stage) return { label: "—", dot: "bg-stone-300" };
  return STATUS_MAP[stage] ?? { label: stage.replace(/^clinic_/, ""), dot: "bg-stone-400" };
}

function shekel(n: number): string {
  return `₪${n.toLocaleString("he-IL")}`;
}

// Removes 18% VAT from a VAT-inclusive price: net = gross / 1.18
const VAT_RATE = 0.18;
function netOfVat(n: number): number {
  return Math.round(n / (1 + VAT_RATE));
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function thisMonthPrefix(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

const monthLabelFmt = new Intl.DateTimeFormat("he-IL", { month: "long", year: "numeric" });
function formatMonthLabel(prefix: string): string {
  const [y, m] = prefix.split("-").map(Number);
  return monthLabelFmt.format(new Date(y, m - 1, 1));
}

const sessionDateFmt = new Intl.DateTimeFormat("he-IL", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

const shortDateFmt = new Intl.DateTimeFormat("he-IL", {
  day: "numeric",
  month: "numeric",
});

function mutationErrorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export function ClinicHomePage() {
  const { data: contacts, isLoading: loadingContacts } =
    useResourceList<Contact>("contacts");
  const { data: sessions, isLoading: loadingSessions } =
    useResourceList<ClinicSession>("clinic_sessions");

  const updateContact = useResourceUpdate("contacts");
  const createContact = useResourceCreate<Contact>("contacts");
  const deleteContact = useResourceDelete("contacts");
  const createSession = useResourceCreate<ClinicSession>("clinic_sessions");
  const updateSession = useResourceUpdate("clinic_sessions");
  const deleteSession = useResourceDelete("clinic_sessions");

  const [editPatient, setEditPatient] = useState<Contact | null>(null);
  const [addSessionFor, setAddSessionFor] = useState<Contact | null>(null);
  const [detailPatient, setDetailPatient] = useState<Contact | null>(null);
  const [addPatientOpen, setAddPatientOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Contact | null>(null);
  const [noteSession, setNoteSession] = useState<ClinicSession | null>(null);
  const [editSession, setEditSession] = useState<ClinicSession | null>(null);
  const [collapsedMonths, setCollapsedMonths] = useState<Set<string>>(new Set());

  function toggleMonthSection(monthPfx: string) {
    setCollapsedMonths((prev) => {
      const next = new Set(prev);
      if (next.has(monthPfx)) next.delete(monthPfx); else next.add(monthPfx);
      return next;
    });
  }

  const patients = (contacts ?? []).filter(
    (c) =>
      typeof c.lifecycle_stage === "string" &&
      c.lifecycle_stage.startsWith("clinic_"),
  );
  const active = patients.filter((p) => p.lifecycle_stage === "clinic_patient");
  const activePatients = patients.filter(
    (p) => p.lifecycle_stage !== "clinic_archive" && p.lifecycle_stage !== "clinic_paused",
  );
  const pausedPatients = patients.filter((p) => p.lifecycle_stage === "clinic_paused");
  const archivedPatients = patients.filter((p) => p.lifecycle_stage === "clinic_archive");

  const monthPrefix = thisMonthPrefix();
  const allSessions = sessions ?? [];
  const thisMonthSessions = allSessions.filter((s) =>
    s.session_date.startsWith(monthPrefix),
  );

  function sessionsForPatient(patientId: number, monthOnly = false): ClinicSession[] {
    const src = monthOnly ? thisMonthSessions : allSessions;
    return src.filter((s) => s.contact_id === patientId);
  }

  function sessionsForPatientInMonth(patientId: number, monthPfx: string): ClinicSession[] {
    return allSessions.filter((s) => s.contact_id === patientId && s.session_date.startsWith(monthPfx));
  }

  function patientsForMonth(monthPfx: string): Contact[] {
    return patients.filter((p) =>
      allSessions.some((s) => s.contact_id === p.id && s.session_date.startsWith(monthPfx)),
    );
  }

  const totalMonthly = active.reduce((sum, p) => {
    const count = sessionsForPatient(p.id, true).length;
    return sum + count * (p.extra?.rate ?? 0);
  }, 0);

  // All past months (not current) that have at least one unpaid patient — sorted most-recent first.
  // When all patients in a month are marked paid, that month auto-disappears.
  const billingMonths = Array.from(
    new Set(allSessions.map((s) => s.session_date.slice(0, 7)).filter((m) => m < monthPrefix)),
  )
    .sort((a, b) => b.localeCompare(a))
    .filter((monthPfx) => {
      const monthPatients = patientsForMonth(monthPfx);
      return monthPatients.some((p) => !(p.extra?.paid_months ?? []).includes(monthPfx));
    });

  // Total outstanding (unpaid gross) across all billing months
  const totalOutstanding = billingMonths.reduce((sum, monthPfx) => {
    return sum + patientsForMonth(monthPfx).reduce((s, p) => {
      if ((p.extra?.paid_months ?? []).includes(monthPfx)) return s;
      return s + sessionsForPatientInMonth(p.id, monthPfx).length * (p.extra?.rate ?? 0);
    }, 0);
  }, 0);

  const unpaidPatientsCount = billingMonths.reduce((count, monthPfx) => {
    return count + patientsForMonth(monthPfx).filter(
      (p) => !(p.extra?.paid_months ?? []).includes(monthPfx),
    ).length;
  }, 0);

  function handleTogglePaid(patient: Contact, month: string) {
    const currentPaid = patient.extra?.paid_months ?? [];
    const isPaid = currentPaid.includes(month);
    const updatedPaid = isPaid
      ? currentPaid.filter((m) => m !== month)
      : [...currentPaid, month];
    updateContact.mutate(
      { id: patient.id, data: { extra_patch: { paid_months: updatedPaid } } },
      { onError: (err) => alert(`שגיאה בעדכון תשלום: ${mutationErrorMessage(err)}`) },
    );
  }

  const isLoading = loadingContacts || loadingSessions;

  function handleConfirmDelete() {
    if (!deleteTarget) return;
    const id = deleteTarget.id;
    deleteContact.mutate(id, {
      onSuccess: () => {
        setDeleteTarget(null);
        if (editPatient?.id === id) setEditPatient(null);
        if (detailPatient?.id === id) setDetailPatient(null);
        if (addSessionFor?.id === id) setAddSessionFor(null);
      },
      onError: (err) => alert(`שגיאה במחיקת מטופל: ${mutationErrorMessage(err)}`),
    });
  }

  return (
    <div className="space-y-6 sm:space-y-7" dir="rtl">
      <header className="pb-2">
        <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-stone-500">
          קליניקה פרטית — דניאל כהן
        </p>
        <h1 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-stone-900">
          המטופלים שלי.
        </h1>
        <p className="mt-3 max-w-xl text-sm sm:text-base text-stone-600 leading-snug">
          כאן יושב הצד השקט של העבודה — האנשים שאתה מלווה.
        </p>
      </header>

      <ColorBlock tint="lilac" size="lg">
        <div className="grid sm:grid-cols-[1fr_auto] gap-8 sm:gap-12 items-end">
          <BigStat
            label="צפי הכנסה החודש (ללא מע״מ)"
            value={isLoading ? "…" : shekel(netOfVat(totalMonthly))}
            size="xl"
            hint={isLoading ? "סה״כ מטיפולים שנרשמו" : `כולל מע״מ: ${shekel(totalMonthly)}`}
          />
          <div className="grid grid-cols-2 gap-6 sm:gap-10">
            <BigStat
              label="מטופלים פעילים"
              value={isLoading ? "…" : active.length}
              size="md"
            />
            <BigStat
              label="טיפולים החודש"
              value={isLoading ? "…" : thisMonthSessions.length}
              size="md"
            />
          </div>
        </div>
      </ColorBlock>

      {(isLoading || billingMonths.length > 0 || totalOutstanding > 0) && (
        <ColorBlock tint="peach" size="md">
          <div className="grid sm:grid-cols-[1fr_auto] gap-8 sm:gap-12 items-end">
            <BigStat
              label="לחיוב — חודשים קודמים (ללא מע״מ)"
              value={isLoading ? "…" : shekel(netOfVat(totalOutstanding))}
              size="lg"
              hint={isLoading ? "…" : `כולל מע״מ: ${shekel(totalOutstanding)}`}
            />
            <BigStat
              label="מטופלים שעוד לא שילמו"
              value={isLoading ? "…" : unpaidPatientsCount}
              size="md"
            />
          </div>
        </ColorBlock>
      )}

      {billingMonths.map((monthPfx) => {
        const monthPatients = patientsForMonth(monthPfx);
        const paidCount = monthPatients.filter((p) =>
          (p.extra?.paid_months ?? []).includes(monthPfx),
        ).length;
        const isOpen = !collapsedMonths.has(monthPfx);
        const label = formatMonthLabel(monthPfx);

        return (
          <div key={monthPfx} className="rounded-[24px] bg-white overflow-hidden">
            <button
              type="button"
              onClick={() => toggleMonthSection(monthPfx)}
              className="w-full flex items-center justify-between gap-4 px-6 py-5 border-b border-stone-200/70 text-start hover:bg-stone-50/60 transition-colors"
            >
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">
                  לחיוב — {label}
                </p>
                <h2 className="mt-1 text-base sm:text-lg font-bold tracking-tight text-stone-900">
                  {monthPatients.length === 0
                    ? `אין טיפולים לחיוב.`
                    : `${monthPatients.length} מטופלים לחיוב${paidCount > 0 ? ` · ${paidCount}/${monthPatients.length} שולמו ✓` : ""}`}
                </h2>
              </div>
              {isOpen ? (
                <ChevronUp className="h-4 w-4 text-stone-400 shrink-0" />
              ) : (
                <ChevronDown className="h-4 w-4 text-stone-400 shrink-0" />
              )}
            </button>
            {isOpen && (
              isLoading ? (
                <div className="p-6 text-sm text-stone-500">טוען…</div>
              ) : (
                <PatientsTable
                  patients={monthPatients}
                  sessionsForPatient={(patientId) => sessionsForPatientInMonth(patientId, monthPfx)}
                  periodLabel={label}
                  onSelect={setDetailPatient}
                  onEdit={setEditPatient}
                  onAddSession={setAddSessionFor}
                  onDelete={setDeleteTarget}
                  onShowNote={setNoteSession}
                  onEditSession={setEditSession}
                  onDeleteSession={(id) =>
                    deleteSession.mutate(id, {
                      onError: (err) => alert(`שגיאה במחיקת טיפול: ${mutationErrorMessage(err)}`),
                    })
                  }
                  onTogglePaid={(patient) => handleTogglePaid(patient, monthPfx)}
                  paidMonth={monthPfx}
                />
              )
            )}
          </div>
        );
      })}

      <div className="rounded-[24px] bg-white overflow-hidden">
        <div className="px-6 py-5 border-b border-stone-200/70 flex items-center justify-between gap-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">
              מטופלים פעילים
            </p>
            <h2 className="mt-1 text-base sm:text-lg font-bold tracking-tight text-stone-900">
              {activePatients.length === 0
                ? "אין כעת מטופלים פעילים."
                : `${activePatients.length} שמות`}
            </h2>
          </div>
          <button
            type="button"
            onClick={() => setAddPatientOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-stone-900 px-3.5 py-2 text-sm font-semibold text-white hover:bg-stone-700 transition-colors shrink-0"
          >
            <UserPlus className="h-3.5 w-3.5" />
            הוסף מטופל
          </button>
        </div>
        {isLoading ? (
          <div className="p-6 text-sm text-stone-500">טוען…</div>
        ) : (
          <PatientsTable
            patients={activePatients}
            sessionsForPatient={sessionsForPatient}
            onSelect={setDetailPatient}
            onEdit={setEditPatient}
            onAddSession={setAddSessionFor}
            onDelete={setDeleteTarget}
            onShowNote={setNoteSession}
            onEditSession={setEditSession}
            onDeleteSession={(id) =>
              deleteSession.mutate(id, {
                onError: (err) => alert(`שגיאה במחיקת טיפול: ${mutationErrorMessage(err)}`),
              })
            }
          />
        )}
      </div>

      <div className="rounded-[24px] bg-white overflow-hidden">
        <div className="px-6 py-5 border-b border-stone-200/70">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">
            מטופלים מושהים
          </p>
          <h2 className="mt-1 text-base sm:text-lg font-bold tracking-tight text-stone-900">
            {pausedPatients.length === 0 ? "אין מטופלים מושהים." : `${pausedPatients.length} שמות`}
          </h2>
        </div>
        {isLoading ? (
          <div className="p-6 text-sm text-stone-500">טוען…</div>
        ) : (
          <PatientsTable
            patients={pausedPatients}
            sessionsForPatient={sessionsForPatient}
            onSelect={setDetailPatient}
            onEdit={setEditPatient}
            onAddSession={setAddSessionFor}
            onDelete={setDeleteTarget}
            onShowNote={setNoteSession}
            onEditSession={setEditSession}
            onDeleteSession={(id) =>
              deleteSession.mutate(id, {
                onError: (err) => alert(`שגיאה במחיקת טיפול: ${mutationErrorMessage(err)}`),
              })
            }
          />
        )}
      </div>

      <div className="rounded-[24px] bg-white overflow-hidden">
        <div className="px-6 py-5 border-b border-stone-200/70">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">
            ארכיון מטופלים
          </p>
          <h2 className="mt-1 text-base sm:text-lg font-bold tracking-tight text-stone-900">
            {archivedPatients.length === 0 ? "אין מטופלים בארכיון." : `${archivedPatients.length} שמות`}
          </h2>
        </div>
        {isLoading ? (
          <div className="p-6 text-sm text-stone-500">טוען…</div>
        ) : (
          <PatientsTable
            patients={archivedPatients}
            sessionsForPatient={sessionsForPatient}
            onSelect={setDetailPatient}
            onEdit={setEditPatient}
            onAddSession={setAddSessionFor}
            onDelete={setDeleteTarget}
            onShowNote={setNoteSession}
            onEditSession={setEditSession}
            onDeleteSession={(id) =>
              deleteSession.mutate(id, {
                onError: (err) => alert(`שגיאה במחיקת טיפול: ${mutationErrorMessage(err)}`),
              })
            }
          />
        )}
      </div>

      <EditPatientModal patient={editPatient} open={editPatient !== null} onOpenChange={(o) => { if (!o) setEditPatient(null); }} onSave={(id, data) => updateContact.mutate({ id, data }, { onSuccess: () => setEditPatient(null), onError: (err) => alert(`שגיאה בשמירת מטופל: ${mutationErrorMessage(err)}`), })} saving={updateContact.isPending} />
      <AddPatientModal open={addPatientOpen} onOpenChange={setAddPatientOpen} onSave={(data) => createContact.mutate(data, { onSuccess: () => setAddPatientOpen(false), onError: (err) => alert(`שגיאה בהוספת מטופל: ${mutationErrorMessage(err)}`), })} saving={createContact.isPending} />
      <AddSessionModal patient={addSessionFor} open={addSessionFor !== null} onOpenChange={(o) => { if (!o) setAddSessionFor(null); }} onSave={(contactId, sessionDate, notes) => createSession.mutate({ contact_id: contactId, session_date: sessionDate, notes: notes || null }, { onSuccess: () => setAddSessionFor(null), onError: (err) => alert(`שגיאה בהוספת טיפול: ${mutationErrorMessage(err)}`), })} saving={createSession.isPending} />
      <PatientDetailModal patient={detailPatient} sessions={detailPatient ? sessionsForPatient(detailPatient.id) : []} open={detailPatient !== null} onOpenChange={(o) => { if (!o) setDetailPatient(null); }} onDeleteSession={(id) => deleteSession.mutate(id, { onError: (err) => alert(`שגיאה במחיקת טיפול: ${mutationErrorMessage(err)}`), })} onRequestDelete={(patient) => setDeleteTarget(patient)} />
      <ConfirmDeleteDialog patient={deleteTarget} open={deleteTarget !== null} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }} onConfirm={handleConfirmDelete} deleting={deleteContact.isPending} />
      <SessionNoteModal session={noteSession} open={noteSession !== null} onOpenChange={(o) => { if (!o) setNoteSession(null); }} />
      <EditSessionDateModal session={editSession} open={editSession !== null} onOpenChange={(o) => { if (!o) setEditSession(null); }} onSave={(id, sessionDate) => updateSession.mutate({ id, data: { session_date: sessionDate } }, { onSuccess: () => setEditSession(null), onError: (err) => alert(`שגיאה בשינוי תאריך: ${mutationErrorMessage(err)}`), })} saving={updateSession.isPending} />
    </div>
  );
}

function PatientsTable({ patients, sessionsForPatient, periodLabel = "החודש", onSelect, onEdit, onAddSession, onDelete, onShowNote, onEditSession, onDeleteSession, onTogglePaid, paidMonth }: {
  patients: Contact[];
  sessionsForPatient: (patientId: number, monthOnly?: boolean) => ClinicSession[];
  periodLabel?: string;
  onSelect: (p: Contact) => void;
  onEdit: (p: Contact) => void;
  onAddSession: (p: Contact) => void;
  onDelete: (p: Contact) => void;
  onShowNote: (s: ClinicSession) => void;
  onEditSession: (s: ClinicSession) => void;
  onDeleteSession: (id: number) => void;
  onTogglePaid?: (patient: Contact) => void;
  paidMonth?: string;
}) {
  if (patients.length === 0) return null;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr>
            {["שם", "סטטוס", "תעריף", `טיפולים ${periodLabel}`, "תאריכי פגישות", "צפי תשלום (ללא מע״מ)"].map((h) => (
              <th key={h} className="text-start px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">{h}</th>
            ))}
            {onTogglePaid && (
              <th className="text-start px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">שולם</th>
            )}
            <th className="px-6 py-3" />
          </tr>
        </thead>
        <tbody>
          {patients.map((p) => {
            const status = statusOf(p.lifecycle_stage);
            const rate = p.extra?.rate ?? 0;
            const monthSessions = sessionsForPatient(p.id, true).slice().sort((a, b) => a.session_date.localeCompare(b.session_date));
            const expected = monthSessions.length * rate;
            const fullName = [p.first_name, p.last_name].filter(Boolean).join(" ") || "—";
            const isPaid = paidMonth ? (p.extra?.paid_months ?? []).includes(paidMonth) : false;
            return (
              <tr key={p.id} className={`border-t border-stone-200/70 transition-colors align-middle ${isPaid ? "bg-emerald-50/40 hover:bg-emerald-50/60" : "hover:bg-stone-50"}`}>
                <td className="px-6 py-4">
                  <button type="button" onClick={() => onSelect(p)} className={`font-medium text-start hover:text-stone-600 transition-colors ${isPaid ? "text-stone-400 line-through" : "text-stone-900"}`}>{fullName}</button>
                </td>
                <td className="px-6 py-4">
                  <span className="inline-flex items-center gap-2 text-stone-700">
                    <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`} />
                    {status.label}
                  </span>
                </td>
                <td className="px-6 py-4 text-stone-700 tabular-nums" dir="ltr">{rate ? shekel(rate) : "—"}</td>
                <td className="px-6 py-4 text-stone-700 tabular-nums text-center">{monthSessions.length}</td>
                <td className="px-6 py-4 text-stone-700 tabular-nums" dir="ltr">
                  {monthSessions.length === 0 ? "—" : (
                    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
                      {monthSessions.map((s, i) => {
                        const dateLabel = shortDateFmt.format(new Date(s.session_date.slice(0, 10) + "T12:00:00"));
                        const sep = i < monthSessions.length - 1 ? "," : "";
                        return (
                          <span key={s.id} className="inline-flex items-center gap-0.5 group">
                            {s.notes ? (
                              <button type="button" onClick={() => onShowNote(s)} title={s.notes} className="font-semibold text-amber-600 underline underline-offset-2 hover:text-amber-700 transition-colors">{dateLabel}</button>
                            ) : <span>{dateLabel}</span>}
                            {sep}
                            <button type="button" onClick={() => onEditSession(s)} title="שנה תאריך" className="text-stone-300 opacity-0 group-hover:opacity-100 hover:text-stone-600 transition-colors"><Pencil className="h-3 w-3" /></button>
                            <button type="button" onClick={() => { if (confirm(`למחוק את הטיפול מתאריך ${dateLabel}?`)) onDeleteSession(s.id); }} title="מחק טיפול" className="text-stone-300 opacity-0 group-hover:opacity-100 hover:text-red-500 transition-colors"><Trash2 className="h-3 w-3" /></button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 text-stone-800 tabular-nums font-medium" dir="ltr">
                  {expected ? (
                    <div>
                      <div>{shekel(netOfVat(expected))}</div>
                      <div className="text-xs font-normal text-stone-400">כ.מע״מ: {shekel(expected)}</div>
                    </div>
                  ) : "—"}
                </td>
                {onTogglePaid && (
                  <td className="px-6 py-4">
                    <button
                      type="button"
                      onClick={() => onTogglePaid(p)}
                      title={isPaid ? "בטל סימון תשלום" : "סמן כשולם + חשבונית"}
                      className={`p-1 rounded-lg transition-colors ${isPaid ? "text-emerald-500 hover:text-emerald-700 hover:bg-emerald-100" : "text-stone-300 hover:text-stone-500 hover:bg-stone-100"}`}
                    >
                      {isPaid
                        ? <CheckCircle2 className="h-5 w-5" />
                        : <Circle className="h-5 w-5" />}
                    </button>
                  </td>
                )}
                <td className="px-6 py-4">
                  <div className="flex items-center gap-1">
                    <button type="button" onClick={() => onEdit(p)} className="p-1.5 rounded-lg text-stone-400 hover:text-stone-700 hover:bg-stone-100 transition-colors" title="ערוך מטופל"><Pencil className="h-3.5 w-3.5" /></button>
                    <button type="button" onClick={() => onAddSession(p)} className="p-1.5 rounded-lg text-stone-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors" title="הוסף טיפול"><Plus className="h-3.5 w-3.5" /></button>
                    <button type="button" onClick={() => onDelete(p)} className="p-1.5 rounded-lg text-stone-400 hover:text-red-600 hover:bg-red-50 transition-colors" title="מחק מטופל"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function EditPatientModal({ patient, open, onOpenChange, onSave, saving }: { patient: Contact | null; open: boolean; onOpenChange: (open: boolean) => void; onSave: (id: number, data: Record<string, unknown>) => void; saving: boolean; }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [stage, setStage] = useState("clinic_patient");
  const [rate, setRate] = useState("");
  useEffect(() => { if (open && patient) { setFirstName(patient.first_name ?? ""); setLastName(patient.last_name ?? ""); setStage(patient.lifecycle_stage ?? "clinic_patient"); setRate(String(patient.extra?.rate ?? "")); } }, [open, patient]);
  if (!patient) return null;
  function handleSave() { if (!patient) return; const rateNum = rate ? parseInt(rate, 10) : null; onSave(patient.id, { first_name: firstName || null, last_name: lastName || null, lifecycle_stage: stage || null, extra_patch: rateNum !== null && !isNaN(rateNum) ? { rate: rateNum } : undefined }); }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" dir="rtl">
        <DialogHeader><DialogTitle className="text-xl font-bold">עריכת מטופל</DialogTitle></DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500 block mb-1">שם פרטי</label><input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-stone-300" /></div>
            <div><label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500 block mb-1">שם משפחה</label><input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-stone-300" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500 block mb-1">סטטוס</label><select value={stage} onChange={(e) => setStage(e.target.value)} className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-stone-300 bg-white">{STAGE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
            <div><label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500 block mb-1">תעריף (₪)</label><input type="number" value={rate} onChange={(e) => setRate(e.target.value)} className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-stone-300" dir="ltr" /></div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => onOpenChange(false)} className="rounded-lg px-4 py-2 text-sm font-medium text-stone-600 hover:bg-stone-100 transition-colors">ביטול</button>
            <button type="button" onClick={handleSave} disabled={saving} className="rounded-lg bg-stone-900 px-4 py-2 text-sm font-semibold text-white hover:bg-stone-700 transition-colors disabled:opacity-50">{saving ? "שומר…" : "שמור"}</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AddPatientModal({ open, onOpenChange, onSave, saving }: { open: boolean; onOpenChange: (open: boolean) => void; onSave: (data: Record<string, unknown>) => void; saving: boolean; }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [stage, setStage] = useState("clinic_patient");
  const [rate, setRate] = useState("");
  useEffect(() => { if (open) { setFirstName(""); setLastName(""); setStage("clinic_patient"); setRate(""); } }, [open]);
  const canSave = firstName.trim() !== "" || lastName.trim() !== "";
  function handleSave() { if (!canSave) return; const rateNum = rate ? parseInt(rate, 10) : NaN; onSave({ first_name: firstName.trim() || null, last_name: lastName.trim() || null, lifecycle_stage: stage, rate: !isNaN(rateNum) ? rateNum : undefined }); }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" dir="rtl">
        <DialogHeader><DialogTitle className="text-xl font-bold">הוספת מטופל</DialogTitle></DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500 block mb-1">שם פרטי</label><input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-stone-300" /></div>
            <div><label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500 block mb-1">שם משפחה</label><input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-stone-300" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500 block mb-1">סטטוס</label><select value={stage} onChange={(e) => setStage(e.target.value)} className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-stone-300 bg-white">{STAGE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
            <div><label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500 block mb-1">תעריף (₪)</label><input type="number" value={rate} onChange={(e) => setRate(e.target.value)} className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-stone-300" dir="ltr" /></div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => onOpenChange(false)} className="rounded-lg px-4 py-2 text-sm font-medium text-stone-600 hover:bg-stone-100 transition-colors">ביטול</button>
            <button type="button" onClick={handleSave} disabled={saving || !canSave} className="rounded-lg bg-stone-900 px-4 py-2 text-sm font-semibold text-white hover:bg-stone-700 transition-colors disabled:opacity-50">{saving ? "שומר…" : "הוסף מטופל"}</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ConfirmDeleteDialog({ patient, open, onOpenChange, onConfirm, deleting }: { patient: Contact | null; open: boolean; onOpenChange: (open: boolean) => void; onConfirm: () => void; deleting: boolean; }) {
  if (!patient) return null;
  const fullName = [patient.first_name, patient.last_name].filter(Boolean).join(" ") || "—";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm" dir="rtl">
        <DialogHeader><DialogTitle className="text-xl font-bold">מחיקת מטופל</DialogTitle></DialogHeader>
        <div className="space-y-4 pt-2">
          <p className="text-sm text-stone-600 leading-relaxed">למחוק את <span className="font-semibold text-stone-900">{fullName}</span> לצמיתות? כל הטיפולים הרשומים שלו יימחקו גם הם. אין אפשרות לשחזר.</p>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => onOpenChange(false)} className="rounded-lg px-4 py-2 text-sm font-medium text-stone-600 hover:bg-stone-100 transition-colors">ביטול</button>
            <button type="button" onClick={onConfirm} disabled={deleting} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 transition-colors disabled:opacity-50">{deleting ? "מוחק…" : "מחק לצמיתות"}</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AddSessionModal({ patient, open, onOpenChange, onSave, saving }: { patient: Contact | null; open: boolean; onOpenChange: (open: boolean) => void; onSave: (contactId: number, sessionDate: string, notes: string) => void; saving: boolean; }) {
  const [sessionDate, setSessionDate] = useState(todayIso());
  const [notes, setNotes] = useState("");
  useEffect(() => { if (open) { setSessionDate(todayIso()); setNotes(""); } }, [open]);
  if (!patient) return null;
  const fullName = [patient.first_name, patient.last_name].filter(Boolean).join(" ") || "—";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm" dir="rtl">
        <DialogHeader><DialogTitle className="text-xl font-bold">הוספת טיפול</DialogTitle><p className="text-sm text-stone-600 mt-1">{fullName}</p></DialogHeader>
        <div className="space-y-4 pt-2">
          <div><label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500 block mb-1">תאריך טיפול</label><input type="date" value={sessionDate} onChange={(e) => setSessionDate(e.target.value)} className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-stone-300" dir="ltr" /></div>
          <div><label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500 block mb-1">הערות (אופציונלי)</label><textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-stone-300 resize-none" /></div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => onOpenChange(false)} className="rounded-lg px-4 py-2 text-sm font-medium text-stone-600 hover:bg-stone-100 transition-colors">ביטול</button>
            <button type="button" onClick={() => onSave(patient.id, sessionDate, notes)} disabled={saving || !sessionDate} className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 transition-colors disabled:opacity-50">{saving ? "שומר…" : "הוסף טיפול"}</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SessionNoteModal({ session, open, onOpenChange }: { session: ClinicSession | null; open: boolean; onOpenChange: (open: boolean) => void; }) {
  if (!session) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm" dir="rtl">
        <DialogHeader><DialogTitle className="text-xl font-bold">הערה לטיפול</DialogTitle><p className="text-sm text-stone-600 mt-1" dir="ltr">{sessionDateFmt.format(new Date(session.session_date.slice(0, 10) + "T12:00:00"))}</p></DialogHeader>
        <p className="pt-2 text-sm text-stone-700 leading-relaxed whitespace-pre-wrap">{session.notes}</p>
      </DialogContent>
    </Dialog>
  );
}

function EditSessionDateModal({ session, open, onOpenChange, onSave, saving }: { session: ClinicSession | null; open: boolean; onOpenChange: (open: boolean) => void; onSave: (id: number, sessionDate: string) => void; saving: boolean; }) {
  const [sessionDate, setSessionDate] = useState("");
  useEffect(() => { if (open && session) setSessionDate(session.session_date.slice(0, 10)); }, [open, session]);
  if (!session) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm" dir="rtl">
        <DialogHeader><DialogTitle className="text-xl font-bold">שינוי תאריך טיפול</DialogTitle></DialogHeader>
        <div className="space-y-4 pt-2">
          <div><label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500 block mb-1">תאריך טיפול</label><input type="date" value={sessionDate} onChange={(e) => setSessionDate(e.target.value)} className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-stone-300" dir="ltr" /></div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => onOpenChange(false)} className="rounded-lg px-4 py-2 text-sm font-medium text-stone-600 hover:bg-stone-100 transition-colors">ביטול</button>
            <button type="button" onClick={() => onSave(session.id, sessionDate)} disabled={saving || !sessionDate} className="rounded-lg bg-stone-900 px-4 py-2 text-sm font-semibold text-white hover:bg-stone-700 transition-colors disabled:opacity-50">{saving ? "שומר…" : "שמור"}</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PatientDetailModal({ patient, sessions, open, onOpenChange, onDeleteSession, onRequestDelete }: { patient: Contact | null; sessions: ClinicSession[]; open: boolean; onOpenChange: (open: boolean) => void; onDeleteSession: (id: number) => void; onRequestDelete: (patient: Contact) => void; }) {
  if (!patient) return null;
  const fullName = [patient.first_name, patient.last_name].filter(Boolean).join(" ") || "—";
  const status = statusOf(patient.lifecycle_stage);
  const rate = patient.extra?.rate ?? 0;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" dir="rtl">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3"><span className={`h-2 w-2 rounded-full ${status.dot} shrink-0`} /><DialogTitle className="text-xl font-bold tracking-tight">{fullName}</DialogTitle></div>
            <button type="button" onClick={() => onRequestDelete(patient)} className="p-1.5 rounded-lg text-stone-300 hover:text-red-500 hover:bg-red-50 transition-colors shrink-0" title="מחק מטופל"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
          <p className="text-stone-600 text-sm mt-1">{status.label}{rate ? ` · ${shekel(rate)} לטיפול` : ""}</p>
        </DialogHeader>
        <div className="pt-2">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500 mb-3">טיפולים ({sessions.length})</p>
          {sessions.length === 0 ? (
            <p className="text-sm text-stone-400 py-2">אין טיפולים רשומים. לחץ על + בטבלה כדי להוסיף.</p>
          ) : (
            <div className="space-y-1.5 max-h-80 overflow-y-auto">
              {sessions.map((s) => (
                <div key={s.id} className="flex items-center justify-between rounded-lg border border-stone-200/70 bg-stone-50/40 px-3 py-2.5">
                  <div>
                    <p className="text-sm font-medium text-stone-800" dir="ltr">{sessionDateFmt.format(new Date(s.session_date.slice(0, 10) + "T12:00:00"))}</p>
                    {s.notes && <p className="mt-0.5 text-xs text-stone-500">{s.notes}</p>}
                  </div>
                  <button type="button" onClick={() => onDeleteSession(s.id)} className="p-1.5 rounded-lg text-stone-300 hover:text-red-500 hover:bg-red-50 transition-colors shrink-0" title="מחק טיפול"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
